import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Lock, 
  Shield, 
  Smartphone, 
  Mail, 
  Loader, 
  AlertTriangle, 
  Check, 
  Settings 
} from 'lucide-react';
import mfaService from '../../services/mfaService';
import MFASetup from '../Auth/MFASetup';
import toast from 'react-hot-toast';

const SecurityTab = ({ userProfile, embedded = false }) => {
  // MFA-related state
  const [mfaStatus, setMfaStatus] = useState(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [methodToRemove, setMethodToRemove] = useState(null);
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [trustedDevices, setTrustedDevices] = useState([]);

  // Load MFA status
  const loadMFAStatus = async () => {
    try {
      setMfaLoading(true);
      const [status, devices] = await Promise.all([
        mfaService.getMFAStatus(),
        mfaService.getTrustedDevices().catch(() => [])
      ]);
      setMfaStatus(status);
      setTrustedDevices(devices);
    } catch (error) {
      console.error('Error loading MFA status:', error);
    } finally {
      setMfaLoading(false);
    }
  };

  // Handle MFA setup completion
  const handleMFASetupComplete = () => {
    console.log('[SECURITY TAB] MFA setup completed, refreshing status...');
    setShowMfaSetup(false);
    loadMFAStatus();
  };

  // Handle trusted device removal
  const handleRemoveTrustedDevice = async (deviceId) => {
    try {
      await mfaService.removeTrustedDevice(deviceId);
      toast.success('Trusted device removed');
      loadMFAStatus();
    } catch (error) {
      console.error('Error removing trusted device:', error);
      toast.error('Failed to remove trusted device');
    }
  };

  // Handle MFA method removal - show confirmation modal
  const handleRemoveMFAMethod = (methodId) => {
    console.log('[SECURITY TAB] Remove MFA method clicked:', methodId);
    setMethodToRemove(methodId);
    setShowRemoveConfirm(true);
  };

  // Confirm MFA method removal
  const confirmRemoveMFAMethod = async () => {
    if (!methodToRemove || !passwordConfirmation.trim()) {
      toast.error('Please enter your password to confirm');
      return;
    }
    
    console.log('[SECURITY TAB] Confirming removal of method:', methodToRemove);
    setShowRemoveConfirm(false);
    
    try {
      console.log('[SECURITY TAB] Calling mfaService.removeMFAMethod...');
      await mfaService.removeMFAMethod(methodToRemove, passwordConfirmation);
      console.log('[SECURITY TAB] MFA method removed successfully');
      toast.success('MFA method removed successfully');
      
      await loadMFAStatus();
    } catch (error) {
      console.error('[SECURITY TAB] Error removing MFA method:', error);
      
      let errorMessage = 'Failed to remove MFA method';
      
      if (error.response?.status === 400) {
        const errorData = error.response?.data;
        if (errorData?.detail) {
          errorMessage = errorData.detail;
        } else if (errorData?.non_field_errors) {
          errorMessage = errorData.non_field_errors[0];
        } else {
          errorMessage = 'Invalid request. Please try again.';
        }
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to remove this MFA method.';
      } else if (error.response?.status === 404) {
        errorMessage = 'MFA method not found. It may have already been removed.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setMethodToRemove(null);
      setPasswordConfirmation('');
    }
  };

  // Cancel MFA method removal
  const cancelRemoveMFAMethod = () => {
    console.log('[SECURITY TAB] MFA removal cancelled by user');
    setShowRemoveConfirm(false);
    setMethodToRemove(null);
    setPasswordConfirmation('');
  };

  // Load MFA status on mount
  useEffect(() => {
    loadMFAStatus();
  }, []);

  return (
    <div className="space-y-6">
      {/* Header for embedded mode */}
      {embedded && (
        <div className="mb-2">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Security Settings</h2>
          <p className="text-sm text-gray-500 mt-0.5">Manage your password and authentication methods</p>
        </div>
      )}

      {/* Password Section */}
      <div className={embedded ? "bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-5" : "bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden"}>
        {!embedded && (
          <div className="px-6 py-4 bg-primary-600 text-white">
            <h2 className="text-xl font-semibold text-white flex items-center">
              <Lock className="w-5 h-5 mr-2" />
              Password
            </h2>
          </div>
        )}
        <div className={embedded ? "" : "p-6"}>
          {embedded && (
            <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-2">Password</h3>
          )}
          <p className="text-gray-600 mb-4 text-sm">
            Keep your account secure by using a strong, unique password.
          </p>
          <Link 
            to="/change-password"
            className="inline-flex items-center bg-primary-600 text-white px-5 py-2.5 rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
          >
            <Lock className="w-4 h-4 mr-2" />
            Change Password
          </Link>
        </div>
      </div>

      {/* MFA Section */}
      <div className={embedded ? "bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-5" : "bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden"}>
        {!embedded && (
          <div className="px-6 py-4 bg-primary-600 text-white">
            <h2 className="text-xl font-semibold text-white flex items-center">
              <Shield className="w-5 h-5 mr-2" />
              Multi-Factor Authentication
            </h2>
          </div>
        )}
        <div className={embedded ? "" : "p-6"}>
          {embedded && (
            <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-2">Multi-Factor Authentication</h3>
          )}
          {mfaLoading ? (
            <div className="flex justify-center py-8">
              <Loader className="w-6 h-6 animate-spin text-primary-600" />
            </div>
          ) : mfaStatus ? (
            <div className="space-y-6">
              {/* MFA Status Overview */}
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full mr-3 ${
                      mfaStatus.mfa_setup_completed ? 'bg-green-500' : 'bg-yellow-500'
                    }`}></div>
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-gray-100">
                        {mfaStatus.mfa_setup_completed 
                          ? 'MFA Enabled' 
                          : (mfaStatus.mfa_required ? 'MFA Setup Required' : 'MFA Available')
                        }
                      </h3>
                      <p className="text-sm text-gray-600">
                        {mfaStatus.mfa_setup_completed 
                          ? 'Your account is protected with multi-factor authentication' 
                          : (mfaStatus.mfa_required 
                              ? 'Multi-factor authentication is required for your account'
                              : 'Multi-factor authentication is optional for your account'
                            )
                        }
                      </p>
                    </div>
                  </div>
                </div>

                {/* MFA Required Notice */}
                {mfaStatus.mfa_required && !mfaStatus.mfa_setup_completed && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start">
                      <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
                      <div>
                        <p className="text-sm text-yellow-800 font-medium">
                          MFA Setup Required
                        </p>
                        <p className="text-sm text-yellow-700 mt-1">
                          {mfaStatus.required_by_study 
                            ? `MFA is required by your study administrator. ${mfaStatus.setup_deadline ? `Setup deadline: ${new Date(mfaStatus.setup_deadline).toLocaleDateString()}` : ''}` 
                            : 'MFA is required for your account.'
                          }
                        </p>
                        <button
                          onClick={() => setShowMfaSetup(true)}
                          className="mt-2 bg-yellow-600 text-white px-4 py-2 rounded-md text-sm hover:bg-yellow-700 transition-colors"
                        >
                          Set Up MFA Now
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* MFA Methods */}
                {mfaStatus.mfa_setup_completed && mfaStatus.mfa_methods && mfaStatus.mfa_methods.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Active MFA Methods</h4>
                    <div className="space-y-2">
                      {mfaStatus.mfa_methods.map((method, index) => {
                        const isOnlyMethod = mfaStatus.mfa_methods.length === 1;
                        const isRemoveDisabled = isOnlyMethod && mfaStatus.mfa_required;
                        
                        return (
                          <div key={method.id || index} className="flex items-center justify-between bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                            <div className="flex items-center">
                              {method.type === 'totp' ? (
                                <Smartphone className="w-5 h-5 text-primary-600 mr-3" />
                              ) : (
                                <Mail className="w-5 h-5 text-primary-600 mr-3" />
                              )}
                              <div>
                                <p className="font-medium text-gray-900 dark:text-gray-100">
                                  {method.type === 'totp' ? 'Authenticator App' : 'Email Verification'}
                                </p>
                                <p className="text-sm text-gray-600">
                                  {method.type === 'totp' 
                                    ? `Device: ${method.device_name || 'Unknown Device'}` 
                                    : `Codes sent to ${userProfile?.email}`
                                  }
                                </p>
                                <p className="text-xs text-gray-500">
                                  {method.is_primary && <span className="text-primary-600 font-medium">Primary • </span>}
                                  Added: {new Date(method.created_at).toLocaleDateString()}
                                  {method.last_used && (
                                    <span> • Last used: {new Date(method.last_used).toLocaleDateString()}</span>
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Check className="w-5 h-5 text-green-600" />
                              <button
                                onClick={() => handleRemoveMFAMethod(method.id)}
                                disabled={isRemoveDisabled}
                                className={`text-sm font-medium ${
                                  isRemoveDisabled 
                                    ? 'text-gray-400 cursor-not-allowed' 
                                    : 'text-red-600 hover:text-red-700'
                                }`}
                                title={isRemoveDisabled ? 'Cannot remove your only MFA method when MFA is required' : 'Remove this MFA method'}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Add Another Device Button */}
                    <div className="text-center mt-4">
                      <button
                        onClick={() => setShowMfaSetup(true)}
                        className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors duration-300 flex items-center justify-center mx-auto text-sm"
                      >
                        <Shield className="w-4 h-4 mr-2" />
                        Add Another Device
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Setup MFA Button - Only show if MFA is not required (optional setup) */}
              {!mfaStatus.mfa_setup_completed && !mfaStatus.mfa_required && (
                <div className="text-center">
                  <button
                    onClick={() => setShowMfaSetup(true)}
                    className="bg-primary-600 text-white px-6 py-3 rounded-full hover:bg-primary-700 transition-colors duration-300 flex items-center justify-center mx-auto"
                  >
                    <Shield className="w-5 h-5 mr-2" />
                    Set Up Multi-Factor Authentication
                  </button>
                </div>
              )}

              {/* Trusted Devices */}
              {trustedDevices && trustedDevices.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                    <Settings className="w-4 h-4 mr-2" />
                    Trusted Devices
                  </h4>
                  <div className="space-y-2">
                    {trustedDevices.map((device) => (
                      <div key={device.id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">
                            {device.device_name || 'Unknown Device'}
                          </p>
                          <p className="text-sm text-gray-600">
                            Added: {new Date(device.created_at).toLocaleDateString()}
                            {device.expires_at && (
                              <span> • Expires: {new Date(device.expires_at).toLocaleDateString()}</span>
                            )}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRemoveTrustedDevice(device.id)}
                          className="text-red-600 hover:text-red-700 text-sm font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Unable to load MFA status</p>
              <button
                onClick={loadMFAStatus}
                className="mt-2 text-primary-600 hover:text-primary-700 font-medium"
              >
                Retry
              </button>
            </div>
          )}
        </div>
      </div>

      {/* MFA Setup Modal */}
      {showMfaSetup && (
        <MFASetup
          onSetupComplete={handleMFASetupComplete}
          onCancel={mfaStatus?.mfa_required || userProfile?.role === 'Program Admin' ? null : () => setShowMfaSetup(false)}
          userEmail={userProfile?.email}
          isRequired={mfaStatus?.mfa_required || userProfile?.role === 'Program Admin'}
        />
      )}

      {/* MFA Remove Confirmation Modal */}
      {showRemoveConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Remove MFA Method
            </h3>
            <p className="text-gray-600 mb-4">
              {mfaStatus.mfa_methods.length > 1 
                ? 'Are you sure you want to remove this MFA method? This will reduce your account security.'
                : 'Are you sure you want to remove this MFA method? You will need to set up another MFA method before you can remove this one if MFA is required for your account.'
              }
            </p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter your password to confirm:
              </label>
              <input
                type="password"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Enter your current password"
                autoFocus
              />
            </div>
            
            <div className="flex space-x-3 justify-end">
              <button
                onClick={cancelRemoveMFAMethod}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveMFAMethod}
                disabled={!passwordConfirmation.trim()}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityTab;
